import { HelioSDKError } from './Error';

export class ApiAdapter {
  constructor(
    private baseUrl: string,
    private apiKey: string,
    private secretKey: string,
  ) {}

  async get<T>(
    url: string,
    errorMessage?: string,
    query?: Record<string, string>,
  ): Promise<T> {
    const queryString = query
      ? `&${new URLSearchParams(query).toString()}`
      : '';

    const response = await fetch(
      `${this.baseUrl}${url}?apiKey=${this.apiKey}${queryString}`,
      { headers: this.getHeaders() },
    );

    if (!response.ok) {
      throw new HelioSDKError(
        `${errorMessage || `Failed to get ${url}`}: ${response.statusText}`,
      );
    }

    return this.parseJSON<T>(response);
  }

  async post<T>(url: string, body: any, errorMessage?: string): Promise<T> {
    const response = await fetch(
      `${this.baseUrl}${url}?apiKey=${this.apiKey}`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      },
    );

    if (!response.ok) {
      throw new HelioSDKError(
        `${errorMessage || `Failed to post ${url}`}: ${response.statusText}`,
      );
    }

    return this.parseJSON<T>(response);
  }

  async patch<T>(url: string, body?: any, errorMessage?: string): Promise<T> {
    const response = await fetch(
      `${this.baseUrl}${url}?apiKey=${this.apiKey}`,
      {
        method: 'PATCH',
        headers: this.getHeaders(),
        ...(body ? { body: JSON.stringify(body) } : {}),
      },
    );

    if (!response.ok) {
      throw new HelioSDKError(
        `${errorMessage || `Failed to patch ${url}`}: ${response.statusText}`,
      );
    }

    return this.parseJSON<T>(response);
  }

  async delete<T>(url: string, errorMessage?: string): Promise<T> {
    const response = await fetch(
      `${this.baseUrl}${url}?apiKey=${this.apiKey}`,
      { method: 'DELETE', headers: this.getHeaders() },
    );

    if (!response.ok) {
      throw new HelioSDKError(
        `${errorMessage || `Failed to delete ${url}`}: ${response.statusText}`,
      );
    }

    return this.parseJSON<T>(response);
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.secretKey}`,
    };
  }

  private async parseJSON<T>(response: Response): Promise<T> {
    const statusIsNoContent =
      response.status === 204 || response.status === 205;

    const contentType = response.headers.get('content-type');
    const text = await response.text();

    const dataIsEmpty = !text;

    const isJson = contentType?.includes('application/json');

    // Handle empty responses or non-JSON responses
    if (dataIsEmpty || !isJson || statusIsNoContent) {
      return undefined as T;
    }

    // Parse JSON from the text we already read
    try {
      return JSON.parse(text) as T;
    } catch (error) {
      // If JSON parsing fails, return undefined
      return undefined as T;
    }
  }
}
